/**
 * CustomerNameField — reusable MUI Autocomplete for customer-name entry
 * across creation, feedback, and hours-saved flows.
 *
 * Props:
 *   - value: string — current value
 *   - onChange: (newValue: string) => void
 *   - required: boolean — whether the field is required
 *   - designs: Design[] — array of designs to extract existing customerName values from
 *   - disabled: boolean — disabled state
 *   - label: string — field label (default "Customer name")
 *   - placeholder: string — placeholder text
 */
import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

export default function CustomerNameField({
  value,
  onChange,
  required = false,
  designs = [],
  disabled = false,
  autoFocus = false,
  label = 'Customer name',
  placeholder = 'e.g. Luma',
}) {
  // Extract distinct non-empty customerName values from designs, case-insensitive deduped, sorted.
  const options = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const d of designs) {
      const name = (d.customerName || '').trim();
      if (!name) continue;
      const lower = name.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      list.push(name);
    }
    return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [designs]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value || ''}
      onChange={(_, newValue) => onChange?.(newValue || '')}
      onInputChange={(_, newInputValue) => onChange?.(newInputValue || '')}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          required={required}
          variant="outlined"
          size="small"
          fullWidth
          autoFocus={autoFocus}
          // No floating MUI label — the dialogs render their own native
          // label above the field, and the shrunk in-border label read as
          // a duplicate. The accessible name comes from aria-label.
          inputProps={{ ...params.inputProps, 'aria-label': label }}
        />
      )}
      sx={{
        // Match the app's .modal-input look — the MUI default notched
        // outline is near-invisible against the modal surface, which
        // made this field read as borderless next to native inputs.
        '& .MuiOutlinedInput-root': {
          fontSize: 13,
          background: 'var(--surface)',
          borderRadius: '8px',
          color: 'var(--text-primary)',
          '& fieldset': { border: '1px solid var(--border-strong)' },
          '&:hover fieldset': { border: '1px solid var(--border-strong)' },
          '&.Mui-focused fieldset': { border: '1px solid var(--accent-blue)' },
        },
        '& .MuiOutlinedInput-input::placeholder': {
          color: 'var(--text-tertiary)',
          opacity: 1,
        },
      }}
    />
  );
}
